import { FC, useState } from 'react';
import { Routes } from '../../../router/Routes';
import { SiteHeader } from '../../../components/ui/SiteHeader';
import { Form, Formik } from 'formik';
import PaidIcon from '@mui/icons-material/Paid';
import { LaunchpadCard } from '../../../components/ui/LaunchpadCard';
import { DefaultFastTextField } from '../../../components/mui-overrides/Textfield';
import { Button, Typography } from '@mui/material';
import { TwoColumnGridContainer } from '../../../components/ui/TwoColumnGridContainer';
import { patchRevenuePool, postRevenuePool } from '../../../api/stoApi';
import { useNavigate, useParams } from 'react-router-dom';
import { generalStore } from '../../../stores/GeneralStore';
import { UploadButton } from '../../../components/ui/UploadButton';
import { AssetFormActionsContainer } from '../../../components/assets/form-components/AssetFormActionsContainer';
import { useRevenuePool } from '../../../hooks/useRevenuePool';
import { RevenuePoolAssetsTable } from './RevenuePoolAssetsTable';
import { withZodSchema } from 'formik-validator-zod';
import { RevenuePoolSchema } from '@brickwise/asset-validation/dist/revenue-pool/revenue-pool';
import { useTranslation } from 'react-i18next';
import { CurrencyFieldText } from '../../../components/mui-overrides/Currency';

export const RevenuePoolCreateEdit: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { poolId: id } = useParams();
  const [reload, setReload] = useState(false);
  const revenuePool = useRevenuePool(id || '', reload);

  return (
    <>
      <SiteHeader
        breadcrumbs={[
          {
            link: Routes.REVENUE_POOLS.ROOT,
            label: t('revenuePools.overview.routeLabel'),
            icon: <PaidIcon sx={{ mr: 0.5 }} fontSize="inherit" />,
          },
          {
            link: Routes.REVENUE_POOLS.ADD,
            label: revenuePool ? t('revenuePools.detail.edit.routeLabel') : t('revenuePools.detail.add.routeLabel'),
          },
        ]}
        headline={revenuePool ? revenuePool?.title || '…' : t('revenuePools.detail.add.title')}
      ></SiteHeader>
      {((id && revenuePool) || !id) && (
        <Formik
          enableReinitialize
          initialValues={{
            title: revenuePool?.title || '',
            fileUrl: revenuePool?.fileUrl || undefined,
            rentalReturn: revenuePool?.rentalReturn || undefined,
          }}
          validate={withZodSchema(RevenuePoolSchema)}
          onSubmit={async (values) => {
            try {
              await (revenuePool ? patchRevenuePool(id || '', values) : postRevenuePool(values));
              if (id) {
                setReload(!reload);
              } else {
                navigate(Routes.REVENUE_POOLS.ROOT);
              }
              generalStore.setInfoMsg(revenuePool ? 'Updated revenue pool.' : 'Created new revenue pool.');
            } catch (err) {
              generalStore.setError('An error occured.');
            }
          }}
        >
          {(props) => {
            return (
              <Form>
                <TwoColumnGridContainer>
                  <LaunchpadCard title={t('revenuePools.detail.information')}>
                    <DefaultFastTextField label={t('revenuePools.detail.form.props.title')} fieldName="title" />
                    <CurrencyFieldText
                      hideHelperText={true}
                      label={t('revenuePools.detail.form.props.rentalReturn')}
                      fieldName="rentalReturn"
                      formik={props}
                      currency={'%'}
                    />
                  </LaunchpadCard>
                  <LaunchpadCard title={t('revenuePools.detail.form.props.document.title')}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 0' }}>
                      {props.values.fileUrl ? (
                        <Typography variant="button">{t('revenuePools.detail.form.props.document.label.uploaded')}</Typography>
                      ) : (
                        <Typography variant="button">{t('revenuePools.detail.form.props.document.label.notUploaded')}</Typography>
                      )}
                      <UploadButton
                        disabled={false}
                        fileUrl={props.values.fileUrl || ''}
                        setFileUrl={(fileUrl) => props.setFieldValue('fileUrl', fileUrl || null)}
                        text={t('revenuePools.detail.form.props.document.upload')}
                      />
                    </div>
                  </LaunchpadCard>
                </TwoColumnGridContainer>
                <AssetFormActionsContainer>
                  <Button variant="contained" color="inherit" disableElevation onClick={() => navigate(Routes.REVENUE_POOLS.ROOT)}>
                    {t('general.back')}
                  </Button>
                  <Button variant="contained" color="primary" disableElevation disabled={false} type="submit">
                    {t('general.save')}
                  </Button>
                </AssetFormActionsContainer>
                {revenuePool && (
                  <LaunchpadCard title={'Assets'}>{<RevenuePoolAssetsTable revenuePool={revenuePool} reload={reload} />}</LaunchpadCard>
                )}
              </Form>
            );
          }}
        </Formik>
      )}
    </>
  );
};
